// Press feedback for every control (port of the unified-design interaction kit): on pointer-down the
// element gets [data-pressed] with a size-aware scale and the touch point for the highlight; the
// state lasts at least 90 ms so a quick tap is still visible. The action itself never waits.

const PRESSABLE = [
  '.btn', '.icon-btn', '.key', '.tab', '.tab-add', '.chip-btn', '.ai-ask', '.qa-row', '.picker-row', 'button.tx', 'a.tx',
  '.segmented > button', '.nav-item', '.plan-line-main', '.cat-line-main', '.wallet-card', '.period-step', '.qa-amount',
  '.plan-check', '.hero-balance', '.debts-card', '.budget-edit', '.link', '.limit-add', '.wallet-name', '.goal-btn', '.link-like',
].join(', ');
const MIN_PRESS_MS = 90;

type PressEl = HTMLElement & { _pressAt?: number; _pressTimer?: number };

const isDisabled = (el: HTMLElement) => (el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true';

/** about 4px off the perimeter whatever the size; list rows and cards move less */
function pressScale(el: HTMLElement) {
  const r = el.getBoundingClientRect();
  const s = 1 - 8 / (r.width + r.height);
  return Math.min(0.985, Math.max(0.9, s)).toFixed(4);
}

function on(el: PressEl, x?: number, y?: number) {
  if (isDisabled(el)) return;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--press-scale', pressScale(el));
  el.style.setProperty('--press-x', x === undefined ? '50%' : `${(((x - r.left) / r.width) * 100).toFixed(1)}%`);
  el.style.setProperty('--press-y', y === undefined ? '50%' : `${(((y - r.top) / r.height) * 100).toFixed(1)}%`);
  el._pressAt = performance.now();
  clearTimeout(el._pressTimer);
  el.setAttribute('data-pressed', '');
}

function off(el: PressEl | null) {
  if (!el?.hasAttribute('data-pressed')) return;
  const wait = Math.max(0, MIN_PRESS_MS - (performance.now() - (el._pressAt ?? 0)));
  clearTimeout(el._pressTimer);
  el._pressTimer = window.setTimeout(() => {
    el.removeAttribute('data-pressed');
    // once the bloom has faded, the touch point is forgotten so the element is back at rest
    el._pressTimer = window.setTimeout(() => {
      el.style.removeProperty('--press-x');
      el.style.removeProperty('--press-y');
    }, 620);
  }, wait);
}

let started = false;
export function initPress() {
  if (started) return;
  started = true;
  // one entry per finger: quick taps on the keypad overlap (the next key goes down before the last one is
  // up), and a single shared slot lost the first key, which then kept its highlight until pressed again
  const active = new Map<number, PressEl>();
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const el = (e.target as Element).closest?.(PRESSABLE) as PressEl | null;
    if (!el) return;
    off(active.get(e.pointerId) ?? null);
    active.set(e.pointerId, el);
    on(el, e.clientX, e.clientY);
  }, { passive: true });
  const release = (e: PointerEvent) => { off(active.get(e.pointerId) ?? null); active.delete(e.pointerId); };
  const releaseAll = () => { active.forEach((el) => off(el)); active.clear(); };
  window.addEventListener('pointerup', release, { passive: true });
  window.addEventListener('pointercancel', release, { passive: true });
  // a scroll that starts on a row is not a press
  window.addEventListener('scroll', releaseAll, { passive: true, capture: true });
  // the app sent to the background mid-press never gets the pointerup
  window.addEventListener('blur', releaseAll);
  const key = (e: KeyboardEvent) => {
    if (e.repeat || (e.key !== ' ' && e.key !== 'Enter')) return;
    const el = (e.target as Element).closest?.(PRESSABLE) as PressEl | null;
    if (!el) return;
    if (e.type === 'keydown') on(el); else off(el);
  };
  document.addEventListener('keydown', key);
  document.addEventListener('keyup', key);
}
